import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { encode, decode } from '../utils/encryptionUtils';

const createMutex = () => {
  let lock = Promise.resolve();

  return function queue(fn) {
    const run = () => fn().catch(console.error);
    const current = lock;
    lock = lock.then(() => run());
    return current;
  };
};

// Shared mutex across hook instances
const updateMutex = createMutex();

const useEncryptedURLState = (key, defaultValue, options = {}) => {
  const { encrypt = true } = options;
  const history = useHistory();
  const location = useLocation();

  const getInitialState = () => {
    const searchParams = new URLSearchParams(location.search);
    const raw = searchParams.get(key);

    if (!raw) return typeof defaultValue === 'function' ? defaultValue() : defaultValue;

    if (!encrypt) {
      try {
        // Try to parse JSON so numbers/booleans/null round-trip correctly
        return JSON.parse(raw);
      } catch (e) {
        // Fallback to plain string for legacy/non-JSON values
        return raw;
      }
    }

    try {
      const decrypted = decode(raw);
      return JSON.parse(decrypted);
    } catch (e) {
      // If value isn't encrypted/JSON, treat it as a plain string
      return raw;
    }
  };

  const [state, setState] = useState(getInitialState);

  const updateState = (newValue) => {
    updateMutex(async () => {
      const resolvedValue = typeof newValue === 'function' ? newValue(state) : newValue;
      setState(resolvedValue);

      const currentParams = new URLSearchParams(history.location.search);
      if (encrypt) {
        const encryptedValue = encode(JSON.stringify(resolvedValue));
        currentParams.set(key, encryptedValue);
      } else {
        // Serialize to JSON to preserve type information in URL
        const serializedValue = JSON.stringify(resolvedValue);
        currentParams.set(key, serializedValue);
      }

      history.replace({
        pathname: location.pathname,
        search: currentParams.toString(),
      });
    });
  };

  return [state, updateState];
};

export default useEncryptedURLState;
